import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Button from '../../components/ui/Button'
import Card from '../../components/ui/Card'
import PageHeader from '../../components/ui/PageHeader'
import { FormGrid } from '../../components/ui/FormField'
import FormTextField from '../../components/ui/form/FormTextField'
import FormTextareaField from '../../components/ui/form/FormTextareaField'
import FormDropdownField from '../../components/ui/form/FormDropdownField'
import { useToast } from '../../components/ui/ToastProvider'
import { useZodForm } from '../../lib/forms/useZodForm'
import { applyServerFieldError } from '../../lib/forms/setServerErrors'
import { useTicketLookups } from './hooks/useTicketLookups'
import { useCreateTicketRequest } from './hooks/useCreateTicketRequest'
import { OTHER_TYPE_ID, ticketRequestDefaults, ticketRequestSchema } from './ticketRequestSchema'
import type { TicketRequestForm } from './ticketRequestSchema'
import { TICKET_REQUEST_STATUS, type TicketRequestCreate } from './types'
import './tickets.css'

/**
 * Bilet Talep Takip → Yeni Talep ekranı (020: şema-tabanlı, react-hook-form + zod). Salt-oluşturma
 * formu; başarıda liste ekranına dönülür. Durum kullanıcı seçimli (zorunlu) — yalnız "Beklemede" (300)
 * veya "Reddedildi" (302). "Diğer" (-1) tür seçilince ayrı zorunlu açıklama alanı görünür ve genel
 * Gerekçe'den ayrı `otherRequestNote` olarak gönderilir (008).
 */
export default function TicketRequestNewPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const lookups = useTicketLookups()
  const create = useCreateTicketRequest()

  const { control, handleSubmit, watch, setError } = useZodForm(ticketRequestSchema, {
    defaultValues: ticketRequestDefaults,
  })
  const navTimer = useRef<number | null>(null)

  // Başarı sonrası yönlendirme zamanlayıcısını unmount'ta temizle.
  useEffect(
    () => () => {
      if (navTimer.current !== null) window.clearTimeout(navTimer.current)
    },
    [],
  )

  const onValid = (values: TicketRequestForm) => {
    const body: TicketRequestCreate = {
      pnr: values.pnr,
      nameSurname: values.nameSurname,
      typeId: values.typeId as number,
      officeId: values.officeId as number,
      statusId: values.statusId as number,
      ticketPrice: values.ticketPrice.trim() ? Number(values.ticketPrice) : null,
      requestNote: values.requestNote,
      // "Diğer" (-1) ise ayrı açıklama; değilse gönderilmez (sunucu null yazar).
      otherRequestNote: values.typeId === OTHER_TYPE_ID ? values.otherRequestNote : null,
    }

    create.mutate(body, {
      onSuccess: () => {
        toast.show({ severity: 'success', summary: t('tickets.new.success'), life: 3000 })
        // Kısa gecikme: başarı toast'ı görülür, sonra listeye dönülür. Buton bu pencerede
        // create.isSuccess ile devre dışı kalır (çift gönderim engeli — FR-011).
        navTimer.current = window.setTimeout(() => navigate('/tickets/list'), 900)
      },
      onError: (err) => {
        // Sunucu 400 alan hatasını ilgili alana eşle; alan-dışı ise merkezî bildirime (019) düşer.
        applyServerFieldError(err, setError)
        toast.show({ severity: 'error', summary: t('tickets.new.errors.submit'), life: 4000 })
      },
    })
  }

  const typeOptions = (lookups.data?.types ?? []).map((ty) => ({ label: ty.name, value: ty.id }))
  const officeOptions = (lookups.data?.offices ?? []).map((o) => ({ label: o.name, value: o.id }))
  // Durum: oluşturmada yalnız Beklemede (300) ve Reddedildi (302) sunulur.
  const statusOptions = (lookups.data?.statuses ?? [])
    .filter((s) => s.id === TICKET_REQUEST_STATUS.PENDING || s.id === TICKET_REQUEST_STATUS.REJECTED)
    .map((s) => ({ label: s.name, value: s.id }))

  const isOther = watch('typeId') === OTHER_TYPE_ID

  return (
    <div className="page tickets-page">
      {/* Başlık = menü adı (anayasa Page Standards). */}
      <PageHeader title={t('nav.bilet.new')} />

      <form onSubmit={handleSubmit(onValid)} noValidate>
        <Card title={t('tickets.new.panelTitle')} className="tk-form-card">
          <FormGrid>
            <FormTextField
              control={control}
              name="pnr"
              label={t('tickets.new.fields.pnr')}
              required
              maxLength={6}
              placeholder={t('tickets.new.placeholders.pnr')}
            />

            <FormTextField
              control={control}
              name="nameSurname"
              label={t('tickets.new.fields.nameSurname')}
              required
              maxLength={255}
              placeholder={t('tickets.new.placeholders.nameSurname')}
            />

            <FormDropdownField
              control={control}
              name="typeId"
              label={t('tickets.new.fields.type')}
              required
              options={typeOptions}
              placeholder={t('tickets.new.select')}
              panelClassName="tk-type-panel"
            />

            <FormDropdownField
              control={control}
              name="officeId"
              label={t('tickets.new.fields.office')}
              required
              options={officeOptions}
              placeholder={t('tickets.new.select')}
            />

            <FormDropdownField
              control={control}
              name="statusId"
              label={t('tickets.new.fields.status')}
              required
              options={statusOptions}
              placeholder={t('tickets.new.select')}
            />

            <FormTextField
              control={control}
              name="ticketPrice"
              label={t('tickets.new.fields.price')}
              inputMode="numeric"
              placeholder={t('tickets.new.placeholders.price')}
            />

            {/* "Diğer" açıklaması: tam-genişlik textarea, hemen Gerekçe'nin üstünde. */}
            {isOther && (
              <FormTextareaField
                control={control}
                name="otherRequestNote"
                label={t('tickets.new.fields.otherType')}
                required
                full
                maxLength={4000}
                placeholder={t('tickets.new.placeholders.otherType')}
              />
            )}

            <FormTextareaField
              control={control}
              name="requestNote"
              label={t('tickets.new.fields.note')}
              required
              full
              placeholder={t('tickets.new.placeholders.note')}
            />
          </FormGrid>

          <div className="tk-form-actions">
            <Button
              type="button"
              variant="secondary"
              label={t('tickets.new.cancel')}
              onClick={() => navigate('/tickets/list')}
            />
            <Button
              type="submit"
              variant="primary"
              label={t('tickets.new.submit')}
              loading={create.isPending}
              disabled={create.isPending || create.isSuccess}
            />
          </div>
        </Card>
      </form>
    </div>
  )
}
